<script lang="ts" setup>
import { ref } from 'vue';
import { formatDateTimeStringToIso } from '@/utils/date-utils';
import {
  BButton,
  BForm,
  BFormGroup,
  BFormInput,
  BSpinner,
} from 'bootstrap-vue-next';
import { OrganizationType } from '@/gql/graphql';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import InviteCreate from '@/components/invite/InviteCreate.vue';
import InvitesList from '@/components/invite/InvitesList.vue';
import OrganizationPicker from '@/components/organization/OrganizationPicker.vue';

// --- Component references ---
const InvitesListRef = ref<InstanceType<typeof InvitesList>>();

// New invite state
const newInviteOrg = ref<OrganizationType | null>(null);
const showNewInviteModal = ref(false);

// Filtering and paging state
const inviteId = ref<number | null>(null);
const userIdInvited = ref<number | null>(null);
const userIdInviter = ref<number | null>(null);
const organizationId = ref<number | null>(null);
const userRole = ref<string | null>(null);
const statusPattern = ref('');
const emailPattern = ref('');

const dateCreatedAfter = ref<string | null>(null);
const dateCreatedBefore = ref<string | null>(null);
const timeCreatedAfter = ref<string | null>(null);
const timeCreatedBefore = ref<string | null>(null);
const dateTimeCreatedAfter = ref<string | null>(null);
const dateTimeCreatedBefore = ref<string | null>(null);

const dateExpiredAfter = ref<string | null>(null);
const dateExpiredBefore = ref<string | null>(null);
const timeExpiredAfter = ref<string | null>(null);
const timeExpiredBefore = ref<string | null>(null);
const dateTimeExpiredAfter = ref<string | null>(null);
const dateTimeExpiredBefore = ref<string | null>(null);

const dateAcceptedAfter = ref<string | null>(null);
const dateAcceptedBefore = ref<string | null>(null);
const timeAcceptedAfter = ref<string | null>(null);
const timeAcceptedBefore = ref<string | null>(null);
const dateTimeAcceptedAfter = ref<string | null>(null);
const dateTimeAcceptedBefore = ref<string | null>(null);

const dateRevokedAfter = ref<string | null>(null);
const dateRevokedBefore = ref<string | null>(null);
const timeRevokedAfter = ref<string | null>(null);
const timeRevokedBefore = ref<string | null>(null);
const dateTimeRevokedAfter = ref<string | null>(null);
const dateTimeRevokedBefore = ref<string | null>(null);

const dateCanceledAfter = ref<string | null>(null);
const dateCanceledBefore = ref<string | null>(null);
const timeCanceledAfter = ref<string | null>(null);
const timeCanceledBefore = ref<string | null>(null);
const dateTimeCanceledAfter = ref<string | null>(null);
const dateTimeCanceledBefore = ref<string | null>(null);

function clearFilter() {
  inviteId.value = null;
  userIdInviter.value = null;
  userIdInvited.value = null;
  organizationId.value = null;
  userRole.value = '';
  statusPattern.value = '';
  emailPattern.value = '';

  dateCreatedBefore.value = null;
  timeCreatedBefore.value = null;
  dateTimeCreatedBefore.value = null;

  dateCreatedAfter.value = null;
  timeCreatedAfter.value = null;
  dateTimeCreatedAfter.value = null;

  dateCanceledBefore.value = null;
  timeCanceledBefore.value = null;
  dateTimeCanceledBefore.value = null;

  dateCanceledAfter.value = null;
  timeCanceledAfter.value = null;
  dateTimeCanceledAfter.value = null;

  dateAcceptedBefore.value = null;
  timeAcceptedBefore.value = null;
  dateTimeAcceptedBefore.value = null;

  dateAcceptedAfter.value = null;
  timeAcceptedAfter.value = null;
  dateTimeAcceptedAfter.value = null;

  dateRevokedBefore.value = null;
  timeRevokedBefore.value = null;
  dateTimeRevokedBefore.value = null;

  dateRevokedAfter.value = null;
  timeRevokedAfter.value = null;
  dateTimeRevokedAfter.value = null;

  dateExpiredBefore.value = null;
  timeExpiredBefore.value = null;
  dateTimeExpiredBefore.value = null;

  dateExpiredAfter.value = null;
  timeExpiredAfter.value = null;
  dateTimeExpiredAfter.value = null;

  requestSearch();
}

function requestSearch() {
  // check integer values are integer values
  if (!Number.isInteger(inviteId.value)) {
    console.warn('Invalid task ID. Resetting to null.');
    inviteId.value = null;
  }
  if (!Number.isInteger(userIdInvited.value)) {
    console.warn('Invalid parent ID. Resetting to null.');
    userIdInvited.value = null;
  }
  if (!Number.isInteger(userIdInviter.value)) {
    console.warn('Invalid user ID. Resetting to null.');
    userIdInviter.value = null;
  }
  if (!Number.isInteger(organizationId.value)) {
    console.warn('Invalid organization ID. Resetting to null.');
    organizationId.value = null;
  }

  // convert date and time to dateTime object (current timezone)
  dateTimeCreatedBefore.value = formatDateTimeStringToIso(
    dateCreatedBefore.value,
    timeCreatedBefore.value
  );
  dateTimeCreatedAfter.value = formatDateTimeStringToIso(
    dateCreatedAfter.value,
    timeCreatedAfter.value
  );
  dateTimeAcceptedBefore.value = formatDateTimeStringToIso(
    dateAcceptedBefore.value,
    timeAcceptedBefore.value
  );
  dateTimeAcceptedAfter.value = formatDateTimeStringToIso(
    dateAcceptedAfter.value,
    timeAcceptedAfter.value
  );
  dateTimeCanceledBefore.value = formatDateTimeStringToIso(
    dateCanceledBefore.value,
    timeCanceledBefore.value
  );
  dateTimeCanceledAfter.value = formatDateTimeStringToIso(
    dateCanceledAfter.value,
    timeCanceledAfter.value
  );
  dateTimeExpiredBefore.value = formatDateTimeStringToIso(
    dateExpiredBefore.value,
    timeExpiredBefore.value
  );
  dateTimeExpiredAfter.value = formatDateTimeStringToIso(
    dateExpiredAfter.value,
    timeExpiredAfter.value
  );
  dateTimeRevokedBefore.value = formatDateTimeStringToIso(
    dateRevokedBefore.value,
    timeRevokedBefore.value
  );
  dateTimeRevokedAfter.value = formatDateTimeStringToIso(
    dateRevokedAfter.value,
    timeRevokedAfter.value
  );

  // refetch the tasks with updated filters
  InvitesListRef.value?.requestSearch();
}
</script>

<template>
  <!-- List compute tasks -->
  <h1 class="mb-4">
    <font-awesome-icon :icon="['fas', 'list-check']" /> Invites Management
  </h1>
  <!-- Content -->
  <div>
    <!-- New Invite -->

    <h3>
      <font-awesome-icon :icon="['fas', 'user-plus']" class="me-2" />
      Create Invite
    </h3>
    <div class="mb-4">
      <OrganizationPicker
        v-model="newInviteOrg"
        class="mb-2"
        label="Select organization to invite user to"
        :allowNullOrganization="false"
        :includeOwnerFullName="true"
        :includeOwnerEmail="true"
        :includeCountryCode="true"
      />
      <BButton
        variant="outline-dark"
        :disabled="!newInviteOrg"
        @click="showNewInviteModal = true"
      >
        <font-awesome-icon :icon="['fas', 'plus']" class="me-2" />
        Invite New User
      </BButton>
    </div>

    <!-- Filter -->
    <h3>
      <font-awesome-icon :icon="['fas', 'envelope']" class="me-2" />
      List of Invites
    </h3>
    <div class="my-3">
      <BForm
        class="d-flex flex-row align-items-end flex-wrap gap-2"
        @submit.prevent="requestSearch"
      >
        <!-- Invite ID -->
        <BFormGroup label="Invite ID" label-for="invite-id">
          <BFormInput
            id="invite-id"
            type="number"
            v-model.number="inviteId"
            placeholder="Invite ID"
          />
        </BFormGroup>

        <!-- User ID Invited -->
        <BFormGroup label="User ID Invited" label-for="user-id-invited">
          <BFormInput
            id="user-id-invited"
            type="number"
            v-model.number="userIdInvited"
            placeholder="User ID Invited"
          />
        </BFormGroup>

        <!-- User ID Inviter -->
        <BFormGroup label="User ID Inviter" label-for="user-id-inviter">
          <BFormInput
            id="user-id-inviter"
            type="number"
            v-model.number="userIdInviter"
            placeholder="User ID Inviter"
          />
        </BFormGroup>

        <!-- Organization ID -->
        <BFormGroup label="Organization ID" label-for="organization-id">
          <BFormInput
            id="organization-id"
            type="number"
            v-model.number="organizationId"
            placeholder="Organization ID"
          />
        </BFormGroup>

        <!-- User Role -->
        <BFormGroup label="User Role" label-for="user-role">
          <BFormInput
            id="user-role"
            type="text"
            v-model="userRole"
            placeholder="e.g. admin, member"
          />
        </BFormGroup>

        <!-- Status Pattern -->
        <BFormGroup>
          <label for="status-pattern">
            Status pattern
            <font-awesome-icon
              id="status-pattern-tooltip-icon"
              icon="question-circle"
              class="ms-1 text-secondary"
            />
          </label>
          <BFormInput
            id="status-pattern"
            type="text"
            v-model="statusPattern"
            placeholder="e.g. pending, accepted, canceled, revoked"
          />
        </BFormGroup>

        <!-- Email Pattern -->
        <BFormGroup label="Email Pattern" label-for="email-pattern">
          <BFormInput
            id="email-pattern"
            type="text"
            v-model="emailPattern"
            placeholder="e.g. user@example.com or user%@example.com"
          />
        </BFormGroup>

        <!-- Date created after -->
        <BFormGroup label="Date created after" label-for="date-created-after">
          <BFormInput
            id="date-created-after"
            type="date"
            v-model="dateCreatedAfter"
          />
        </BFormGroup>
        <BFormGroup label="Time created after" label-for="time-created-after">
          <BFormInput
            id="time-created-after"
            type="time"
            v-model="timeCreatedAfter"
          />
        </BFormGroup>

        <!-- Date created before -->
        <BFormGroup label="Date created before" label-for="date-created-before">
          <BFormInput
            id="date-created-before"
            type="date"
            v-model="dateCreatedBefore"
          />
        </BFormGroup>
        <BFormGroup label="Time created before" label-for="time-created-before">
          <BFormInput
            id="time-created-before"
            type="time"
            v-model="timeCreatedBefore"
          />
        </BFormGroup>

        <!-- Date accepted after -->
        <BFormGroup label="Date accepted after" label-for="date-accepted-after">
          <BFormInput
            id="date-accepted-after"
            type="date"
            v-model="dateAcceptedAfter"
          />
        </BFormGroup>
        <BFormGroup label="Time accepted after" label-for="time-accepted-after">
          <BFormInput
            id="time-accepted-after"
            type="time"
            v-model="timeAcceptedAfter"
          />
        </BFormGroup>

        <!-- Date accepted before -->
        <BFormGroup
          label="Date accepted before"
          label-for="date-accepted-before"
        >
          <BFormInput
            id="date-accepted-before"
            type="date"
            v-model="dateAcceptedBefore"
          />
        </BFormGroup>
        <BFormGroup
          label="Time accepted before"
          label-for="time-accepted-before"
        >
          <BFormInput
            id="time-accepted-before"
            type="time"
            v-model="timeAcceptedBefore"
          />
        </BFormGroup>

        <!-- Date canceled after -->
        <BFormGroup label="Date canceled after" label-for="date-canceled-after">
          <BFormInput
            id="date-canceled-after"
            type="date"
            v-model="dateCanceledAfter"
          />
        </BFormGroup>
        <BFormGroup label="Time canceled after" label-for="time-canceled-after">
          <BFormInput
            id="time-canceled-after"
            type="time"
            v-model="timeCanceledAfter"
          />
        </BFormGroup>

        <!-- Date canceled before -->
        <BFormGroup
          label="Date canceled before"
          label-for="date-canceled-before"
        >
          <BFormInput
            id="date-canceled-before"
            type="date"
            v-model="dateCanceledBefore"
          />
        </BFormGroup>
        <BFormGroup
          label="Time canceled before"
          label-for="time-canceled-before"
        >
          <BFormInput
            id="time-canceled-before"
            type="time"
            v-model="timeCanceledBefore"
          />
        </BFormGroup>

        <!-- Date revoked after -->
        <BFormGroup label="Date revoked after" label-for="date-revoked-after">
          <BFormInput
            id="date-revoked-after"
            type="date"
            v-model="dateRevokedAfter"
          />
        </BFormGroup>
        <BFormGroup label="Time revoked after" label-for="time-revoked-after">
          <BFormInput
            id="time-revoked-after"
            type="time"
            v-model="timeRevokedAfter"
          />
        </BFormGroup>

        <!-- Date revoked before -->
        <BFormGroup label="Date revoked before" label-for="date-revoked-before">
          <BFormInput
            id="date-revoked-before"
            type="date"
            v-model="dateRevokedBefore"
          />
        </BFormGroup>
        <BFormGroup label="Time revoked before" label-for="time-revoked-before">
          <BFormInput
            id="time-revoked-before"
            type="time"
            v-model="timeRevokedBefore"
          />
        </BFormGroup>

        <!-- Date expired after -->
        <BFormGroup label="Date expired after" label-for="date-expired-after">
          <BFormInput
            id="date-expired-after"
            type="date"
            v-model="dateExpiredAfter"
          />
        </BFormGroup>
        <BFormGroup label="Time expired after" label-for="time-expired-after">
          <BFormInput
            id="time-expired-after"
            type="time"
            v-model="timeExpiredAfter"
          />
        </BFormGroup>

        <!-- Date expired before -->
        <BFormGroup label="Date expired before" label-for="date-expired-before">
          <BFormInput
            id="date-expired-before"
            type="date"
            v-model="dateExpiredBefore"
          />
        </BFormGroup>
        <BFormGroup label="Time expired before" label-for="time-expired-before">
          <BFormInput
            id="time-expired-before"
            type="time"
            v-model="timeExpiredBefore"
          />
        </BFormGroup>

        <!-- Filter action buttons -->
        <BButton variant="outline-secondary" @click="clearFilter">
          <font-awesome-icon icon="trash" class="me-1" />
          Clear filter
        </BButton>
        <BButton
          variant="outline-dark"
          type="submit"
          :disabled="InvitesListRef?.loadingInvitesQuery"
        >
          <span v-if="InvitesListRef?.loadingInvitesQuery">
            <b-spinner small label=""></b-spinner>
            Searching
          </span>
          <span v-else>
            <font-awesome-icon icon="magnifying-glass" class="me-1" />
            Search
          </span>
        </BButton>
      </BForm>
    </div>

    <!-- LIST INVITES -->
    <InvitesList
      ref="InvitesListRef"
      :invitedUserId="userIdInvited"
      :inviterUserId="userIdInviter"
      :organizationId="organizationId"
      :userRole="userRole"
      :statusPattern="statusPattern"
      :emailPattern="emailPattern"
      :showStatus="true"
      :adminView="true"
    />

    <!-- CREATE INVITE -->
    <InviteCreate
      v-if="newInviteOrg"
      :organization="newInviteOrg"
      :adminView="true"
      :show="showNewInviteModal"
      @close="showNewInviteModal = false"
      @success="requestSearch()"
    />
  </div>
</template>
